/**
 * Mandatory Update Modal
 * Blocks app usage until user updates to the latest version
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Download, RefreshCw, AlertTriangle, CheckCircle, Loader2, Smartphone } from 'lucide-react';
import appUpdateService from '../services/appUpdateService';

const MandatoryUpdateModal = ({ onUpdateComplete }) => {
  const [checking, setChecking] = useState(true);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    checkForUpdates();
  }, []);

  const checkForUpdates = async () => {
    setChecking(true);
    setError(null);
    
    try {
      const result = await appUpdateService.checkForUpdate();
      console.log('[MandatoryUpdate] Check result:', result);
      
      if (result.error) {
        // If there's an error checking, allow app to continue (graceful degradation)
        console.warn('[MandatoryUpdate] Error checking updates, allowing app to continue');
        setShowModal(false);
        if (onUpdateComplete) onUpdateComplete();
        return;
      }
      
      if (result.updateAvailable) {
        setUpdateInfo(result);
        setShowModal(true);
      } else {
        // No update needed, proceed with app
        setShowModal(false);
        if (onUpdateComplete) onUpdateComplete();
      }
    } catch (err) {
      console.error('[MandatoryUpdate] Check failed:', err);
      // On error, allow app to continue
      setShowModal(false);
      if (onUpdateComplete) onUpdateComplete();
    } finally {
      setChecking(false);
    }
  };

  const handleDownload = async () => {
    if (!updateInfo?.downloadUrl) {
      setError('Download URL not available. Please try again later.');
      return;
    }

    setDownloading(true);
    setError(null);

    try {
      const result = await appUpdateService.downloadAndInstall(
        updateInfo.downloadUrl,
        updateInfo.apkName
      );

      if (result.success) {
        if (result.method === 'browser' || result.method === 'browser_fallback') {
          setError('Download started in browser. Please install the APK manually after download completes.');
        }
        // For native install, the app will close/restart automatically
      } else {
        setError(result.error || 'Download failed. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Show loading while checking
  if (checking) {
    return (
      <Dialog open={true}>
        <DialogContent className="max-w-sm bg-surface" onInteractOutside={(e) => e.preventDefault()}>
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
            <p className="text-lg font-medium text-primary">Checking for updates...</p>
            <p className="text-sm text-muted-foreground mt-2">Please wait</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Don't render if no update needed
  if (!showModal || !updateInfo) {
    return null;
  }

  return (
    <Dialog open={true}>
      <DialogContent 
        className="max-w-md bg-surface" 
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
            Update Required
          </DialogTitle>
          <DialogDescription>
            A new version of FOS Franchise is available. Please update to continue using the app.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Version Info */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Smartphone className="h-5 w-5 text-orange-600" />
                <span className="font-medium text-orange-900">Version Update</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Current Version</p>
                <p className="font-semibold text-gray-700">{updateInfo.currentVersion}</p>
              </div>
              <div>
                <p className="text-gray-500">New Version</p>
                <p className="font-semibold text-green-700">{updateInfo.latestVersion}</p>
              </div>
            </div>

            {updateInfo.apkSize > 0 && (
              <div className="mt-3 pt-3 border-t border-orange-200">
                <p className="text-xs text-gray-500">
                  Download size: {appUpdateService.formatSize(updateInfo.apkSize)}
                </p>
              </div>
            )}
          </div>

          {/* Release Notes */}
          {updateInfo.releaseNotes && (
            <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
              <p className="text-xs font-semibold text-gray-500 mb-1">What's New:</p>
              <p className="text-sm text-gray-700 whitespace-pre-line">
                {updateInfo.releaseNotes.split('###')[0].trim()}
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-2 pt-2">
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="w-full h-12 bg-primary text-white hover:bg-primary/90"
              data-testid="download-update-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-2" />
                  Download & Install Update
                </>
              )}
            </Button>

            <Button
              onClick={checkForUpdates}
              disabled={checking || downloading}
              variant="outline"
              className="w-full"
              data-testid="retry-update-check-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
              Check Again
            </Button>
          </div>

          {/* Footer Note */}
          <p className="text-xs text-center text-muted-foreground">
            The app cannot be used until updated to the latest version.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MandatoryUpdateModal;
